// src/components/HSCodeList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HSCodeList, fetchHSCodeDetail } from "../actions/hscodeActions";

import PaginationControls from "./PaginationControls";
import HSCodeUpdateBulk from "./HSCodeUpdateBulk";
import HSCodeDetail from "./HSCodeDetail";

import { Search, Filter, X } from "lucide-react";

export default function HSCodeListComponent() {
  const dispatch = useDispatch();
  const {
    loading,
    hscodeList,
    count = 0,
    next,
    previous,
    error,
  } = useSelector((state) => state.hscodeList);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  // Filters
  const [profit, setProfit] = useState("");
  const [priority, setPriority] = useState("");
  const [customsDutyRate, setCustomsDutyRate] = useState("");
  const [suq, setSuq] = useState("");

  // Search (debounced)
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // Selected rows (codes)
  const [selectedCodes, setSelectedCodes] = useState([]);

  // Detail overlay
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    const filters = { profit, priority, customsDutyRate, suq, search: query };
    dispatch(HSCodeList(currentPage, pageSize, filters));
  }, [
    dispatch,
    currentPage,
    pageSize,
    profit,
    priority,
    customsDutyRate,
    suq,
    query,
  ]);

  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => next && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => previous && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleFilterChange = (e, setter) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (code) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const clearSelections = () => setSelectedCodes([]);

  const activeFiltersCount = [
    profit,
    priority,
    customsDutyRate,
    suq,
    query,
  ].filter(Boolean).length;
  const clearAllFilters = () => {
    setProfit("");
    setPriority("");
    setCustomsDutyRate("");
    setSuq("");
    setSearchText("");
    setQuery("");
    setCurrentPage(1);
  };

  const openDetail = (code) => {
    if (!code) return;
    dispatch(fetchHSCodeDetail(code));
    setDetailOpen(true);
  };

  return (
    <div dir="rtl" className="mt-24 px-3 md:px-6 font-vazir">
      {/* Card container */}
      <div className="mx-auto bg-white/70 backdrop-blur border border-white/30 shadow-xl rounded-2xl">
        {/* Header */}
        <div className="px-4 md:px-6 pt-5 pb-4 border-b">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                فهرست کدهای HS
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                جستجو، فیلتر، مشاهده جزئیات و به‌روزرسانی دسته‌ای.
              </p>
            </div>

            {/* Quick filter summary / clear */}
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <span className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                  {activeFiltersCount} فیلتر فعال
                </span>
              )}
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-gray-700 hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  پاک کردن
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 text-right mb-2">
              جستجو (کد / نام فارسی / نام انگلیسی)
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="مثال: 732690 / پیچ و مهره / bolt"
                className="w-full rounded-xl border px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white"
              />
              <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Filters */}
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2 text-gray-600">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">فیلترها</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  سود بازرگانی
                </label>
                <select
                  value={profit}
                  onChange={(e) => handleFilterChange(e, setProfit)}
                  className="w-full rounded-xl border px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {[
                    "",
                    "0",
                    "1",
                    "4",
                    "6",
                    "9",
                    "11",
                    "16",
                    "18",
                    "28",
                    "51",
                  ].map((v) => (
                    <option key={v} value={v}>
                      {v === "" ? "همه" : v}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  اولویت
                </label>
                <select
                  value={priority}
                  onChange={(e) => handleFilterChange(e, setPriority)}
                  className="w-full rounded-xl border px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {["", "2", "4", "21", "22", "23", "24", "25", "26", "27"].map(
                    (v) => (
                      <option key={v} value={v}>
                        {v === "" ? "همه" : v}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  حقوق گمرکی
                </label>
                <select
                  value={customsDutyRate}
                  onChange={(e) => handleFilterChange(e, setCustomsDutyRate)}
                  className="w-full rounded-xl border px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {["", "4", "1"].map((v) => (
                    <option key={v} value={v}>
                      {v === "" ? "همه" : v}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">واحد</label>
                <select
                  value={suq}
                  onChange={(e) => handleFilterChange(e, setSuq)}
                  className="w-full rounded-xl border px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {["", "Kg", "L", "U", "m", "m2"].map((v) => (
                    <option key={v} value={v}>
                      {v === "" ? "همه" : v}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 md:px-6 py-4">
          {/* Loading / Error */}
          {loading && (
            <p className="text-blue-600 animate-pulse text-center">
              در حال بارگذاری…
            </p>
          )}
          {error && (
            <p className="text-red-600 text-center">خطا: {String(error)}</p>
          )}

          {!loading && !error && (
            <>
              {!hscodeList || hscodeList.length === 0 ? (
                <p className="text-gray-600 text-center">موردی یافت نشد.</p>
              ) : (
                <>
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    pageSizeOptions={[40, 50, 100, 200, 500]}
                    hasNext={!!next}
                    hasPrevious={!!previous}
                    onPageChange={handlePageChange}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onPageSizeChange={handlePageSizeChange}
                  />

                  <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="p-3 text-right">انتخاب</th>
                          <th className="p-3 text-right">کد</th>
                          <th className="p-3 text-right">نام فارسی</th>
                          <th className="p-3 text-right">نام انگلیسی</th>
                          <th className="p-3 text-right">سود بازرگانی</th>
                          <th className="p-3 text-right">حقوق گمرکی</th>
                          <th className="p-3 text-right">اولویت</th>
                          <th className="p-3 text-right">تاریخ آپدیت</th>
                          <th className="p-3 text-right">آپدیت‌شده توسط</th>
                          <th className="p-3 text-right">واحد</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hscodeList.map((row) => (
                          <tr
                            key={row.id}
                            className="border-t hover:bg-gray-50 transition cursor-pointer"
                            onClick={() => openDetail(row.code)}
                          >
                            <td
                              className="p-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedCodes.includes(row.code)}
                                onChange={() => handleCheckboxChange(row.code)}
                              />
                            </td>
                            <td
                              className="p-3 font-mono text-blue-700 underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row.code);
                              }}
                            >
                              {row.code}
                            </td>
                            <td className="p-3">{row.goods_name_fa}</td>
                            <td className="p-3">{row.goods_name_en}</td>
                            <td className="p-3">{row.profit}</td>
                            <td className="p-3">{row.customs_duty_rate}</td>
                            <td className="p-3">{row.priority}</td>
                            <td className="p-3">{row.updated_date}</td>
                            <td className="p-3">{row.updated_by}</td>
                            <td className="p-3">{row.SUQ}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    pageSizeOptions={[40, 50, 100, 200, 500]}
                    hasNext={!!next}
                    hasPrevious={!!previous}
                    onPageChange={handlePageChange}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onPageSizeChange={handlePageSizeChange}
                  />

                  {/* Bulk update + actions */}
                  <div className="mt-6 rounded-2xl border bg-white shadow-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-900">
                        به‌روزرسانی دسته‌ای
                      </h3>
                      {selectedCodes.length > 0 && (
                        <button
                          onClick={clearSelections}
                          className="text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                        >
                          پاک کردن انتخاب‌ها
                        </button>
                      )}
                    </div>
                    <HSCodeUpdateBulk codes={selectedCodes} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail overlay */}
      {detailOpen && <HSCodeDetail onClose={() => setDetailOpen(false)} />}
    </div>
  );
}
